import { FunctionComponent, SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";

import { faCircleDashed } from "@fortawesome/pro-regular-svg-icons";

import { applicationConfig } from "../../../../../environmentConfiguration";
import { useNavigationService } from "../../../../hooks/useNavigationService";
import { translationKeys } from "../../../../models/app-translation-keys";
import { RouteConstants } from "../../../../models/enums";
import { IMenuItem } from "../../../../models/interfaces";
import { useStore } from "../../../../stores/StoreContext";
import { ActionButton } from "../../Buttons";

import "./FeatureSelectionDropdown.scss";

const FeatureSelectionDropdownRaw: FunctionComponent = () => {
  const { t } = useTranslation();

  const { userStore } = useStore();

  const navigationService = useNavigationService();

  const aiFeatureMenuRef = useRef<Menu>(null);

  const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);

  const menuItemTemplate = (item: MenuItem) => (
    <div
      className="menu-link-feature-selection"
      onClick={event => navigateTo(event, item.url ?? "")}
      onKeyDown={event => event.key === "Enter" && navigateTo(event, item.url ?? "")}
      tabIndex={0}
      role="button"
    >
      <div className="menu-link-icon">{item.icon()}</div>
      {t(item?.label ?? "")}
    </div>
  );

  useEffect(() => {
    const menuItems: IMenuItem[] = navigationService.availableFeatureMenuItems.map(item => {
      return {
        ...item,
        template: menuItemTemplate,
      };
    });
    setMenuItems(menuItems);
  }, [navigationService.availableFeatureMenuItems, menuItemTemplate]);

  const navigate = useNavigate();

  const navigateTo = (event: SyntheticEvent<HTMLDivElement, Event>, path: RouteConstants | string) => {
    navigate(path.startsWith("/") ? path : `/${path}`);
    aiFeatureMenuRef.current?.hide(event);
  };

  if (applicationConfig.featureFlags.enableTermsOfUse && !userStore.userAcceptedTermsOfUseAndPrivacyPolicy) {
    return null; // Do not render the feature selection if terms of use are not accepted and feature flag is enabled
  }

  return (
    <div className="ai-feature-container">
      <Menu model={menuItems} popup={true} ref={aiFeatureMenuRef} id="popup_menu_left" popupAlignment="left" />

      <ActionButton
        id="feature-selection-button"
        label={t(translationKeys.feature.selectAiFeature)}
        onClick={event => aiFeatureMenuRef.current?.toggle(event)}
        icon={faCircleDashed}
      />
    </div>
  );
};

export const FeatureSelectionDropdown = observer(FeatureSelectionDropdownRaw);
